import { ConfigProvider as AntdConfigProvider, App, ThemeConfig } from "antd";
import { ReactNode, useEffect, useState } from "react";
import { useThemeContext } from "./ThemeProvider";

export const VL_CONTROL_HEIGHT_SM = 32;
export const VL_CONTROL_HEIGHT = 40;
export const VL_CONTROL_HEIGHT_LG = 48;

const getTheme = () => {
  const computedStyle = getComputedStyle(document.body);
  const getValue = (name: `--${string}`) =>
    computedStyle.getPropertyValue(name).trim();
  return {
    token: {
      colorText: getValue("--new-text-on-primary"),
      colorPrimary: getValue("--new-primary-button-500"),
      colorPrimaryActive: getValue("--new-primary-button-700"),
      colorPrimaryHover: getValue("--new-primary-button-600"),
      colorSuccess: getValue("--new-success-500"),
      colorSuccessActive: getValue("--new-success-700"),
      colorSuccessHover: getValue("--new-success-600"),
      colorError: getValue("--new-error-500"),
      colorErrorBorder: getValue("--new-error-500"),
      colorErrorActive: getValue("--new-error-700"),
      colorErrorHover: getValue("--new-error-600"),
      colorErrorBorderHover: getValue("--new-error-600"),
      colorWarning: getValue("--new-warning-500"),
      colorWarningActive: getValue("--new-warning-700"),
      colorWarningHover: getValue("--new-warning-600"),
      colorBgElevated: getValue("--new-neutral-cards-300"),
      colorIcon: getValue("--pure-white"),
      colorBgContainerDisabled: getValue("--new-primary-button-400"),
      colorTextDisabled: getValue("--new-text-on-surface-500"),
      borderRadius: 8,
      colorTextPlaceholder: getValue("--new-text-on-surface-500"),
      controlOutline: "none",
    },
    components: {
      Tooltip: {
        fontSize: 14,
        margin: 0,
        colorBgSpotlight: "var(--new-neutral-cards-300)",
        colorTextLightSolid: "var(--new-text-on-primary)",
      },
      Badge: { indicatorHeight: 16, colorBorderBg: "transparent" },
      Input: {
        colorBorder: "var(--new-neutral-900)",
        hoverBg: "transparent",
        hoverBorderColor: "var(--new-primary-blue-500)",
        activeBg: "transparent",
        colorBgContainer: "transparent",
        colorText: "var(--new-text-on-primary)",
      },
      InputNumber: {
        colorBorder: "var(--new-neutral-900)",
        colorBgContainer: "transparent",
        handleBorderColor: "var(--new-neutral-900)",
        handleHoverColor: "var(--new-text-on-primary)",
        handleBg: "var(--new-neutral-background-100)",
      },
      Menu: {
        itemBorderRadius: 12,
        itemBg: getValue("--new-neutral-cards-300"),
        itemHoverBg: getValue("--new-neutral-900"),
        groupTitleColor: getValue("--new-text-on-primary"),
        darkGroupTitleColor: getValue("--new-text-on-primary"),
        itemSelectedColor: getValue("--new-text-on-primary"),
        itemActiveBg: getValue("--new-neutral-900"),
        itemSelectedBg: getValue("--new-neutral-900"),
        itemMarginBlock: 0,
        itemMarginInline: 0,
        zIndexPopup: 100,
        fontSize: 14,
      },
      Popover: {
        colorBgElevated: "var(--new-neutral-1200)",
      },
      Tag: {
        borderRadiusSM: 50,
        colorSuccess: "var(--new-primary-green-500)",
        colorSuccessBg: "var(--new-primary-green-10)",
        colorInfo: "var(--new-text-on-primary)",
        colorInfoBg: "#ffffff1A",
        colorWarning: "var(--new-primary-orange-500)",
        colorWarningBg: "var(--new-primary-orange-10)",
        colorError: "var(--new-primary-red-500)",
        colorErrorBg: "var(--new-primary-red-10)",
      },
      Typography: {
        colorText: "inherit",
        colorLink: "var(--new-primary-blue-500)",
        linkFontSize: 14,
      },
      Form: {
        labelColor: getValue("--new-text-on-surface-500"),
        verticalLabelPadding: 4,
      },
      Table: {
        headerBg: "var(--new-neutral-background-100)",
        bodySortBg: "unset",
        rowSelectedBg: "var(--new-neutral-cards-300)",
        rowSelectedHoverBg: "var(--new-neutral-cards-300)",
        fixedHeaderSortActiveBg: "var(--new-neutral-cards-300)",
        colorBgContainer: "var(--new-neutral-cards-300)",
        rowHoverBg: "var(--new-neutral-cards-300)",
        headerSortHoverBg: "var(--new-neutral-background-100)",
        headerSortActiveBg: "var(--new-neutral-background-100)",
        headerSplitColor: "transparent",
        headerColor: "var(--new-text-on-primary)",
        cellPaddingInlineSM: 8,
        cellPaddingInlineMD: 12,
        cellPaddingBlockMD: 12,
        colorText: "var(--new-text-on-primary)",
        fontSize: 16,
        selectionColumnWidth: 48,
      },
      Pagination: {
        itemActiveColorDisabled: "var(--new-text-on-surface-500)",
        itemActiveBg: "var(--new-neutral-cards-300)",
        itemActiveBorderColor: "var(--new-special-button-100)",
        itemActiveColor: "var(--new-text-on-primary)",
        itemBg: "transparent",
      },
      Button: {
        controlHeightSM: 32,
        controlHeight: 40,
        controlHeightLG: 48,
        fontWeight: 500,
        defaultBg: "transparent",
        defaultHoverBg: "transparent",
        defaultActiveBg: "transparent",
        defaultColor: "var(--new-text-on-primary)",
        defaultHoverBorderColor: getValue("--new-primary-button-500"),
        defaultActiveBorderColor: getValue("--new-primary-button-600"),
        defaultHoverColor: getValue("--new-primary-button-600"),
        borderColorDisabled: getValue("--new-primary-button-400"),
        borderRadius: 8,
        borderRadiusLG: 8,
        paddingInline: 16,
        paddingInlineLG: 16,
        contentFontSizeSM: 14,
        contentFontSizeLG: 16,
        colorLink: "var(--new-text-on-primary)",
        colorLinkHover: "var(--new-primary-blue-500)",
        colorLinkActive: "var(--new-primary-blue-500)",
        colorTextDisabled: "var(--pure-white)",
        colorBgContainer: "transparent",
      },
      Checkbox: {
        colorPrimary: getValue("--new-primary-blue-500"),
        colorBgContainer: "transparent",
        colorBorder: getValue("--new-neutral-900"),
        colorError: getValue("--new-error-500"),
        borderRadiusSM: 2,
        controlInteractiveSize: 16,
        checkboxSize: 16,
        checkboxCheckColor: getValue("--new-primary-blue-500"),
        checkboxBorderRadius: 2,
        controlOutline: getValue("--new-primary-blue-500"),
      },
      Dropdown: {
        colorBgElevated: "var(--new-neutral-1200)",
        colorText: "var(--new-text-on-primary)",
        colorTextDescription: "var(--new-text-on-primary)",
        colorPrimary: "var(--new-text-on-primary)",
        colorIcon: "var(--new-neutral-900-alt)",
        controlItemBgHover: "var(--new-secondary-button-500)",
        controlItemBgActive: "var(--new-secondary-button-600)",
        controlItemBgActiveHover: "var(--new-secondary-button-500)",
        controlHeight: VL_CONTROL_HEIGHT,
        controlPaddingHorizontal: 12,
        colorSplit: "var(--new-neutral-900)",
        paddingBlock: 8,
        marginXS: 0,
        paddingXXS: 0,
      },
      Slider: {
        handleLineWidth: 1,
        handleLineWidthHover: 2,
        railBg: "var(--new-neutral-900)",
        railHoverBg: "var(--new-neutral-900)",
        handleSize: 12,
        trackBg: "var(--new-primary-blue-500)",
        trackHoverBg: "var(--new-primary-blue-500)",
        handleColor: "var(--pure-white)",
        handleActiveColor: "var(--pure-white)",
        colorBgElevated: "var(--pure-white)",
      },
      Progress: {
        remainingColor: "var(--new-neutral-canvas-50)",
        lineBorderRadius: 100,
        defaultColor: "var(--vl-progress-linear-gradient)",
      },
      Radio: {
        buttonBg: "var(--new-neutral-cards-300)",
        buttonCheckedBg: "var(--new-primary-blue-500)",
        fontSize: 14,
        colorBgContainer: "transparent",
      },
      Select: {
        selectorBg: "transparent",
        optionPadding: "4px 8px",
        colorIcon: "var(--new-text-on-primary)",
        colorIconHover: "var(--new-text-on-surface-500)",
        multipleItemBg: "var(--new-neutral-900)",
      },
      Steps: {
        iconSize: VL_CONTROL_HEIGHT_SM,
        iconFontSize: 16,
        customIconSize: VL_CONTROL_HEIGHT_SM,
        customIconFontSize: 16,
        iconSizeSM: 24,
        colorBgContainer: "var(--new-primary-blue-500)",
      },
      Modal: {
        colorIcon: "var(--new-text-on-primary)",
        colorIconHover: "var(--new-text-on-primary)",
        titleFontSize: 20,
        titleColor: "var(--new-text-on-primary)",
        contentBg: "var(--new-neutral-cards-300)",
        headerBg: "var(--new-neutral-cards-300)",
        footerBg: "var(--new-neutral-cards-300)",
        titleLineHeight: "20px",
      },
      Spin: {
        dotSizeLG: 64,
        dotSize: 32,
        dotSizeSM: 20,
      },
      Segmented: {
        colorText: "var(--new-text-on-primary)",
        trackBg: "var(--new-neutral-cards-300)",
        itemSelectedBg: "var(--new-neutral-background-100)",
        itemColor: "var(--new-text-on-primary)",
        itemSelectedColor: "var(--new-text-on-primary)",
        trackPadding: 4,
      },
      DatePicker: {
        cellActiveWithRangeBg: "var(--new-primary-button-700)",
      },
    },
  };
};

export const ConfigProvider = ({ children }: { children?: ReactNode }) => {
  const { theme } = useThemeContext();
  const [themeConfig, setThemeConfig] = useState<ThemeConfig>(() => getTheme());
  useEffect(() => {
    // Need timeout for the css vars to update on the next render
    setTimeout(() => setThemeConfig(getTheme()), 0);
  }, [theme]);
  return (
    <AntdConfigProvider theme={themeConfig}>
      <App>{children}</App>
    </AntdConfigProvider>
  );
};
